import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtNumber } from "../lib/api";
import { TrendingUp, TrendingDown } from "lucide-react";
import { toast, Toaster } from "sonner";

const PRICE_REFRESH_INTERVAL_MS = 30_000; // poll live prices every 30s

const MarketCard = ({ p, onTrade }) => {
  const up = p.change_24h >= 0;
  return (
    <div className="bg-[hsl(var(--surface))] p-6" data-testid={`market-${p.symbol}`}>
      <div className="flex items-center justify-between">
        <span className="overline">{p.symbol}</span>
        {up ? <TrendingUp className="w-4 h-4 text-[hsl(var(--primary))]" /> : <TrendingDown className="w-4 h-4 text-[hsl(var(--accent))]" />}
      </div>
      <div className="font-display text-lg mt-2">{p.name}</div>
      <div className="font-mono text-2xl mt-3">{fmtUSD(p.price_usd)}</div>
      <div className={`text-xs mt-1 font-mono ${up ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--accent))]"}`}>
        {up ? "+" : ""}{p.change_24h.toFixed(2)}% 24h
      </div>
      <div className="grid grid-cols-2 gap-2 mt-5">
        <button onClick={() => onTrade(p.symbol, "buy")}
          className="bg-[hsl(var(--primary))] text-white py-2 text-sm" data-testid={`buy-${p.symbol}`}>Buy</button>
        <button onClick={() => onTrade(p.symbol, "sell")}
          className="border border-[hsl(var(--primary))] text-[hsl(var(--primary))] py-2 text-sm" data-testid={`sell-${p.symbol}`}>Sell</button>
      </div>
    </div>
  );
};

const HoldingRow = ({ h, prices }) => {
  const price = prices.find((x) => x.symbol === h.symbol)?.price_usd || 0;
  const val = h.quantity * price;
  const cost = h.quantity * h.avg_cost_usd;
  const pl = val - cost;
  return (
    <tr className="border-b border-[hsl(var(--border))]" data-testid={`hold-${h.symbol}`}>
      <td className="py-3 px-4 font-medium">{h.symbol} <span className="text-[hsl(var(--text-muted))]">· {h.name}</span></td>
      <td className="py-3 px-4 text-right font-mono">{fmtNumber(h.quantity)}</td>
      <td className="py-3 px-4 text-right font-mono">{fmtUSD(h.avg_cost_usd)}</td>
      <td className="py-3 px-4 text-right font-mono">{fmtUSD(price)}</td>
      <td className="py-3 px-4 text-right font-mono">{fmtUSD(val)}</td>
      <td className={`py-3 px-4 text-right font-mono ${pl >= 0 ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--destructive))]"}`}>
        {pl >= 0 ? "+" : ""}{fmtUSD(pl)}
      </td>
    </tr>
  );
};

const TradeDialog = ({ trade, prices, checking, busy, amount, setAmount, onCancel, onSubmit }) => {
  if (!trade) return null;
  const currentPrice = prices.find((p) => p.symbol === trade.symbol)?.price_usd || 0;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onCancel}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-8 w-full max-w-md space-y-5">
        <div>
          <div className="overline capitalize">{trade.side}</div>
          <h2 className="font-display text-2xl mt-1">{trade.symbol}</h2>
          <div className="font-mono text-sm text-[hsl(var(--text-muted))] mt-1">@ {fmtUSD(currentPrice)}</div>
        </div>
        <div>
          <label className="overline block mb-2">USD amount</label>
          <input type="number" min="1" step="0.01" required value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))] font-mono text-2xl"
            data-testid="trade-amount" autoFocus />
          {checking && trade.side === "buy" && (
            <div className="overline mt-2">Available {fmtUSD(checking.balance)}</div>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} className="border border-[hsl(var(--border))] px-4 py-2 text-sm" data-testid="cancel-trade-btn">Cancel</button>
          <button type="submit" disabled={busy} className="bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm capitalize" data-testid="confirm-trade-btn">
            {busy ? "Processing…" : `${trade.side} ${trade.symbol}`}
          </button>
        </div>
      </form>
    </div>
  );
};

const Crypto = () => {
  const [prices, setPrices] = useState([]);
  const [wallet, setWallet] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [trade, setTrade] = useState(null); // { symbol, side }
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, w, a] = await Promise.all([
      api.get("/crypto/prices"),
      api.get("/crypto/wallet"),
      api.get("/accounts"),
    ]);
    setPrices(p.data);
    setWallet(w.data);
    setAccounts(a.data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(
      () => api.get("/crypto/prices").then(({ data }) => setPrices(data)).catch(() => {}),
      PRICE_REFRESH_INTERVAL_MS
    );
    return () => clearInterval(t);
  }, [load]);

  const handleTradeOpen = useCallback((symbol, side) => {
    setTrade({ symbol, side });
  }, []);

  const handleTradeCancel = useCallback(() => {
    setTrade(null);
    setAmount("");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/crypto/trade", {
        symbol: trade.symbol,
        usd_amount: parseFloat(amount),
        side: trade.side,
      });
      toast.success(`${trade.side === "buy" ? "Bought" : "Sold"} ${trade.symbol}`);
      handleTradeCancel();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Trade failed");
    } finally {
      setBusy(false);
    }
  };

  const portfolioValue = useMemo(
    () =>
      wallet.reduce((s, h) => {
        const p = prices.find((x) => x.symbol === h.symbol);
        return s + h.quantity * (p?.price_usd || 0);
      }, 0),
    [wallet, prices]
  );

  const checking = useMemo(
    () => accounts.find((a) => a.type === "checking"),
    [accounts]
  );

  return (
    <div className="space-y-10">
      <Toaster richColors position="top-right" />
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Wallet</div>
          <h1 className="font-display text-4xl mt-1">Crypto</h1>
        </div>
        <div className="text-right">
          <div className="overline">Portfolio value</div>
          <div className="font-mono text-3xl" data-testid="portfolio-value">{fmtUSD(portfolioValue)}</div>
        </div>
      </div>

      <section>
        <h2 className="font-display text-xl mb-4">Markets · Live from CoinGecko</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[hsl(var(--border))] border border-[hsl(var(--border))]">
          {prices.map((p) => (
            <MarketCard key={p.symbol} p={p} onTrade={handleTradeOpen} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl mb-4">Holdings</h2>
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
          <table className="w-full text-sm">
            <thead className="border-b border-[hsl(var(--border))]">
              <tr className="text-left">
                <th className="overline py-3 px-4">Asset</th>
                <th className="overline py-3 px-4 text-right">Quantity</th>
                <th className="overline py-3 px-4 text-right">Avg cost</th>
                <th className="overline py-3 px-4 text-right">Price</th>
                <th className="overline py-3 px-4 text-right">Value</th>
                <th className="overline py-3 px-4 text-right">P/L</th>
              </tr>
            </thead>
            <tbody>
              {wallet.map((h) => (
                <HoldingRow key={h.symbol} h={h} prices={prices} />
              ))}
              {wallet.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-[hsl(var(--text-muted))]">No holdings yet. Buy crypto above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <TradeDialog
        trade={trade}
        prices={prices}
        checking={checking}
        busy={busy}
        amount={amount}
        setAmount={setAmount}
        onCancel={handleTradeCancel}
        onSubmit={submit}
      />
    </div>
  );
};

export default Crypto;
